import React from "react";
import { ApiProgressIndicatorProps } from "./api-progress-indicator.props";

const ApiProgressIndicator: React.FC<ApiProgressIndicatorProps> = ({
  progress,
  isVisible,
  variant = "linear",
  position = "top",
  message,
  color = "primary",
  height = 4,
  showPercentage = false,
}) => {
  if (!isVisible) return null;

  const colorClasses = {
    primary: { bg: "bg-primary-500", text: "text-primary-500" },
    success: { bg: "bg-green-500", text: "text-green-500" },
    warning: { bg: "bg-yellow-500", text: "text-yellow-500" },
    error: { bg: "bg-red-500", text: "text-red-500" },
  };

  const positionClasses = {
    top: "top-0 left-0 right-0",
    bottom: "bottom-0 left-0 right-0",
    fixed: "top-0 left-0 right-0 fixed",
  };

  // Linear Progress Bar
  if (variant === "linear") {
    return (
      <div
        className={`${positionClasses[position]} ${position === "fixed" ? "fixed" : "absolute"} z-50`}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="w-full bg-gray-200 overflow-hidden"
          style={{ height: `${height}px` }}
        >
          <div
            className={`h-full ${colorClasses[color].bg} transition-all duration-300 ease-out`}
            style={{ width: `${progress}%` }}
          >
            {/* Animated shimmer effect */}
            <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
          </div>
        </div>
        {message && (
          <div className="bg-white shadow-sm px-4 py-2 text-sm text-gray-700 border-b">
            {message}
            {showPercentage && (
              <span className="ml-2 font-semibold">
                {Math.round(progress)}%
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Circular Progress Indicator
  if (variant === "circular") {
    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progress / 100) * circumference;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
        <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center max-w-xs w-full mx-4">
          <div className="relative w-32 h-32">
            <svg
              className="transform -rotate-90 w-32 h-32"
              viewBox="0 0 100 100"
            >
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-100"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className={`${colorClasses[color].text} transition-all duration-500 ease-out`}
                strokeLinecap="round"
              />
            </svg>
            {/* Percentage text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-gray-800 leading-none">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>
          </div>
          {message && (
            <div className="mt-6 flex flex-col items-center">
              <p className="text-base font-semibold text-gray-800 text-center">
                {message}
              </p>
              <div className="flex gap-1 mt-2">
                <div
                  className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <div
                  className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <div
                  className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Minimal Progress Indicator (small bar at top)
  if (variant === "minimal") {
    return (
      <div
        className={`${positionClasses[position]} ${position === "fixed" ? "fixed" : "absolute"} z-50`}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="w-full h-1 bg-transparent overflow-hidden">
          <div
            className={`h-full ${colorClasses[color].bg} transition-all duration-300 ease-out shadow-lg`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default ApiProgressIndicator;
